import React, { createContext, useContext, useState } from "react";
import { Collapse, Grid, Space } from "@arco-design/web-react";
import {
  Align,
  AttributesPanelWrapper,
  Border,
  ClassName,
  ColorPickerField,
  Height,
  Link,
  Padding,
  SwitchField,
  TextField,
  Width,
} from "easy-email-extensions";
import { useBlock, useFocusIdx } from "easy-email-editor";
import ImagePicker from "../ImagePicker";

interface ImagePickerContextValue {
  getToken: () => Promise<string>;
  companyId: string;
}

const ImagePickerContext = createContext<ImagePickerContextValue | null>(null);

export const ImagePickerContextProvider: React.FC<{
  value: ImagePickerContextValue;
  children: React.ReactNode;
}> = ({ value, children }) => (
  <ImagePickerContext.Provider value={value}>
    {children}
  </ImagePickerContext.Provider>
);

const fullWidthOnMobileAdapter = {
  format(obj: any) {
    return Boolean(obj);
  },
  parse(val: any) {
    if (!val) return undefined;
    return "true";
  },
};

export const ImageAttributePanel: React.FC = () => {
  const pickerCtx = useContext(ImagePickerContext);
  const { focusIdx } = useFocusIdx();
  const { focusBlock, change } = useBlock();
  const [pickerOpen, setPickerOpen] = useState(false);

  if (!focusBlock) return null;

  const currentSrc: string = focusBlock.attributes?.src || "";

  const handleSelectImage = (imageUrl: string) => {
    change(`${focusIdx}.attributes.src`, imageUrl);
  };

  return (
    <AttributesPanelWrapper style={{ padding: 0 }}>
      <Collapse defaultActiveKey={["0", "1", "2", "3", "4"]}>
        <Collapse.Item name="1" header="Setting">
          <Space direction="vertical" style={{ width: "100%" }}>
            <div style={{ width: "100%" }}>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                <div style={{ flex: 1 }}>
                  <TextField
                    label="Image source"
                    name={`${focusIdx}.attributes.src`}
                  />
                </div>
                {pickerCtx && (
                  <button
                    type="button"
                    onClick={() => setPickerOpen(true)}
                    style={{
                      height: 32,
                      padding: "0 14px",
                      background: "#165DFF",
                      color: "#fff",
                      border: "none",
                      borderRadius: 4,
                      cursor: "pointer",
                      fontSize: 13,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Pick image
                  </button>
                )}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: "#86909C",
                  marginTop: 4,
                }}
              >
                The image suffix should be .jpg, jpeg, png, gif, etc.
                Otherwise, the picture may not be displayed normally.
              </div>
              {currentSrc && (
                <div
                  style={{
                    marginTop: 8,
                    border: "1px solid var(--color-neutral-3, #e5e6eb)",
                    borderRadius: 4,
                    padding: 4,
                    textAlign: "center",
                    background: "#fafafa",
                  }}
                >
                  <img
                    src={currentSrc}
                    alt="Preview"
                    style={{
                      maxWidth: "100%",
                      maxHeight: 120,
                      objectFit: "contain",
                    }}
                  />
                </div>
              )}
            </div>
            <ColorPickerField
              label="Background color"
              name={`${focusIdx}.attributes.container-background-color`}
              inline
            />
            <SwitchField
              label="Full width on mobile"
              name={`${focusIdx}.attributes.fluid-on-mobile`}
              config={fullWidthOnMobileAdapter}
            />
          </Space>
        </Collapse.Item>

        <Collapse.Item name="0" header="Dimension">
          <Space direction="vertical">
            <Grid.Row>
              <Grid.Col span={11}>
                <Width />
              </Grid.Col>
              <Grid.Col offset={1} span={11}>
                <Height />
              </Grid.Col>
            </Grid.Row>
            <Padding showResetAll />
            <Grid.Row>
              <Grid.Col span={24}>
                <Align />
              </Grid.Col>
            </Grid.Row>
          </Space>
        </Collapse.Item>

        <Collapse.Item name="2" header="Link">
          <Space direction="vertical" style={{ width: "100%" }}>
            <Link />
          </Space>
        </Collapse.Item>

        <Collapse.Item name="3" header="Border">
          <Border />
        </Collapse.Item>

        <Collapse.Item name="4" header="Extra">
          <Grid.Row>
            <Grid.Col span={11}>
              <TextField
                label="title"
                name={`${focusIdx}.attributes.title`}
              />
            </Grid.Col>
            <Grid.Col offset={1} span={11}>
              <TextField
                label="alt"
                name={`${focusIdx}.attributes.alt`}
              />
            </Grid.Col>
          </Grid.Row>
          <Grid.Col span={24}>
            <ClassName />
          </Grid.Col>
        </Collapse.Item>
      </Collapse>

      {pickerCtx && (
        <ImagePicker
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onSelect={handleSelectImage}
          getToken={pickerCtx.getToken}
          companyId={pickerCtx.companyId}
          selectedImage={currentSrc}
        />
      )}
    </AttributesPanelWrapper>
  );
};
